import React from "react";
import {
	ArrayPick,
	Button,
	ClassNameProps,
	Column,
	englishCommaJoinStrings,
	makeClassName,
	Row,
	StyleProps,
	TruncatedText,
} from "@hex-insights/core";
import { FieldState, FormState } from "@hex-insights/forms";
import { Icon, IconContainer } from "../../../../../../Components/Icon";
import {
	ClassYear,
	ClassYearCategory,
	ClassYearCategoryNameFragment,
	ClassYearNameFragment,
	CourseSection,
	HomeRoomSection,
	HomeRoomSectionNameFragment,
	User,
	UserGroup,
	UserGroupNameFragment,
	UserNameAndImageFragment,
} from "../../../../../../Utilities";
import {
	ClassYearCategoryFormat,
	ClassYearFormat,
	CourseSectionFormat,
	HomeRoomSectionFormat,
	UserFormat,
	UserGroupFormat,
} from "../../../../../Format";
import { UserIcon } from "../../../../User";
import { SharingMenuFormValues } from "../../utils";
import styles from "./styles.module.css";

export type PublicOptionProps = {
	formState: FormState<SharingMenuFormValues>;
	fieldState: FieldState<boolean>;
} & Partial<ClassNameProps & StyleProps>;

export function PublicOption({ formState, fieldState, className, style }: PublicOptionProps) {
	const { setValue: baseSetValue } = fieldState;
	const setValue = React.useCallback(() => {
		baseSetValue(true);
		formState.formClearFunctions.includeGuardians();
		formState.formClearFunctions.includeEmployees();
		formState.formClearFunctions.includeAllUsersOfPersonTypes();
		formState.formClearFunctions.classYearCategoryIDs();
		formState.formClearFunctions.classYearIDs();
		formState.formClearFunctions.homeRoomSectionIDs();
		formState.formClearFunctions.courseSectionIDs();
		formState.formClearFunctions.userGroupIDs();
		formState.formClearFunctions.userIDs();
	}, [baseSetValue, formState]);

	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.Globe />
				</IconContainer>
			}
			label="Everyone"
			description="All users including Parents and Staff"
			value={fieldState.value}
			setValue={setValue}
			className={className}
			style={style}
		/>
	);
}

export type PrivateOptionProps = {
	formState: FormState<SharingMenuFormValues>;
	fieldState: FieldState<boolean>;
} & Partial<ClassNameProps & StyleProps>;

export function PrivateOption({ formState, fieldState, className, style }: PrivateOptionProps) {
	const isPrivate =
		!formState.formValues.isPublic &&
		!(
			(formState.formValues.includeGuardians || formState.formValues.includeEmployees) &&
			formState.formValues.includeAllUsersOfPersonTypes
		) &&
		formState.formValues.classYearCategoryIDs.length === 0 &&
		formState.formValues.classYearIDs.length === 0 &&
		formState.formValues.homeRoomSectionIDs.length === 0 &&
		formState.formValues.courseSectionIDs.length === 0 &&
		formState.formValues.userGroupIDs.length === 0 &&
		formState.formValues.userIDs.length === 0;

	const { setValue: baseSetValue } = fieldState;
	const setValue = React.useCallback(() => {
		baseSetValue(false);
		formState.formClearFunctions.includeGuardians();
		formState.formClearFunctions.includeEmployees();
		formState.formClearFunctions.includeAllUsersOfPersonTypes();
		formState.formClearFunctions.classYearCategoryIDs();
		formState.formClearFunctions.classYearIDs();
		formState.formClearFunctions.homeRoomSectionIDs();
		formState.formClearFunctions.courseSectionIDs();
		formState.formClearFunctions.userGroupIDs();
		formState.formClearFunctions.userIDs();
	}, [baseSetValue, formState]);

	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.Lock />
				</IconContainer>
			}
			label="Private"
			description="Only the author"
			value={isPrivate}
			setValue={setValue}
			className={className}
			style={style}
		/>
	);
}

export type ClassYearCategoryOptionProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearCategoryIDs"]>;
	classYearCategory: Pick<ClassYearCategory, "id"> & ClassYearCategoryNameFragment;
};

export function ClassYearCategoryOption({ fieldState, classYearCategory }: ClassYearCategoryOptionProps) {
	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.ChalkboardTeacher />
				</IconContainer>
			}
			label={ClassYearCategoryFormat.name(classYearCategory)}
			value={fieldState.value.includes(classYearCategory.id)}
			setValue={(v) =>
				fieldState.setValue((prev) =>
					v ? [...prev, classYearCategory.id] : prev.filter((e) => e !== classYearCategory.id),
				)
			}
		/>
	);
}

export type ClassYearOptionProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	classYear: Pick<ClassYear, "id"> & ClassYearNameFragment;
};

export function ClassYearOption({ fieldState, classYear }: ClassYearOptionProps) {
	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.ChalkboardTeacher />
				</IconContainer>
			}
			label={ClassYearFormat.name(classYear)}
			value={fieldState.value.includes(classYear.id)}
			setValue={(v) =>
				fieldState.setValue((prev) => (v ? [...prev, classYear.id] : prev.filter((e) => e !== classYear.id)))
			}
		/>
	);
}

export type HomeRoomSectionOptionProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	homeRoomSection: Pick<HomeRoomSection, "id"> & HomeRoomSectionNameFragment;
};

export function HomeRoomSectionOption({ fieldState, homeRoomSection }: HomeRoomSectionOptionProps) {
	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.House />
				</IconContainer>
			}
			label={HomeRoomSectionFormat.name(homeRoomSection)}
			value={fieldState.value.includes(homeRoomSection.id)}
			setValue={(v) =>
				fieldState.setValue((prev) =>
					v ? [...prev, homeRoomSection.id] : prev.filter((e) => e !== homeRoomSection.id),
				)
			}
		/>
	);
}

export type CourseSectionOptionProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	courseSection: Pick<CourseSection, "id" | "namePrefix" | "nameSuffix"> & {
		course: Pick<CourseSection["course"], "id" | "name"> & {
			classYears: ArrayPick<CourseSection["course"]["classYears"], "id" | "name">;
		};
		homeRoomSection: (Pick<HomeRoomSection, "id"> & HomeRoomSectionNameFragment) | null;
	};
};

export function CourseSectionOption({ fieldState, courseSection }: CourseSectionOptionProps) {
	let description = "";
	if (!!courseSection.homeRoomSection) {
		description = HomeRoomSectionFormat.name(courseSection.homeRoomSection);
	} else if (courseSection.course.classYears.length > 0) {
		description = englishCommaJoinStrings(courseSection.course.classYears.map((e) => ClassYearFormat.name(e)));
	}

	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.Chalkboard />
				</IconContainer>
			}
			label={CourseSectionFormat.name(courseSection)}
			value={fieldState.value.includes(courseSection.id)}
			description={description}
			setValue={(v) =>
				fieldState.setValue((prev) => (v ? [...prev, courseSection.id] : prev.filter((e) => e !== courseSection.id)))
			}
		/>
	);
}

export type UserGroupOptionProps = {
	fieldState: FieldState<SharingMenuFormValues["userGroupIDs"]>;
	userGroup: Pick<UserGroup, "id" | "description"> & UserGroupNameFragment;
};

export function UserGroupOption({ fieldState, userGroup }: UserGroupOptionProps) {
	return (
		<SharingOption
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.Users />
				</IconContainer>
			}
			label={UserGroupFormat.name(userGroup)}
			description={userGroup.description}
			value={fieldState.value.includes(userGroup.id)}
			setValue={(v) =>
				fieldState.setValue((prev) => (v ? [...prev, userGroup.id] : prev.filter((e) => e !== userGroup.id)))
			}
		/>
	);
}

export type UserOptionProps = {
	fieldState: FieldState<SharingMenuFormValues["classYearIDs"]>;
	user: Pick<User, "id"> & UserNameAndImageFragment;
};

export function UserOption({ fieldState, user }: UserOptionProps) {
	return (
		<SharingOption
			icon={<UserIcon user={user} size={28} />}
			label={UserFormat.name(user)}
			value={fieldState.value.includes(user.id)}
			setValue={(v) => fieldState.setValue((prev) => (v ? [...prev, user.id] : prev.filter((e) => e !== user.id)))}
		/>
	);
}

type SharingOptionProps = {
	icon?: JSX.Element;
	label: string;
	description?: string;
	value: boolean;
	setValue: (value: boolean) => void;
} & Partial<ClassNameProps & StyleProps>;

function SharingOption({ icon, label, description, value, setValue, className, style }: SharingOptionProps) {
	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setValue(event.target.checked);
		},
		[setValue],
	);

	return (
		<label
			className={makeClassName(
				styles["sharing-option"],
				value ? styles["sharing-option--selected"] : "",
				description ? styles["sharing-option--with-description"] : "",
				className,
			)}
			style={style}
		>
			<Row justify="spaced-start" align="center" className={styles["sharing-option__content"]}>
				{icon}

				<Column className={styles["sharing-option__column"]}>
					<TruncatedText>{label}</TruncatedText>

					{!!description && (
						<TruncatedText className={styles["sharing-option__description"]}>{description}</TruncatedText>
					)}
				</Column>

				<span>
					<input type="checkbox" checked={value} onChange={onChange} />
				</span>
			</Row>
		</label>
	);
}

export type CustomSharingButtonProps = {
	formState: FormState<SharingMenuFormValues>;
	onClick: () => void;
};

export function CustomSharingButton({ formState, onClick }: CustomSharingButtonProps) {
	const isSharingCustom =
		formState.formValues.includeEmployees ||
		formState.formValues.includeGuardians ||
		formState.formValues.includeAllUsersOfPersonTypes ||
		formState.formValues.classYearCategoryIDs.length > 0 ||
		formState.formValues.classYearIDs.length > 0 ||
		formState.formValues.courseSectionIDs.length > 0 ||
		formState.formValues.homeRoomSectionIDs.length > 0 ||
		formState.formValues.userIDs.length > 0;

	return (
		<SharingOptionButton
			icon={
				<IconContainer className={styles["sharing-option__icon-container"]}>
					<Icon.Gear />
				</IconContainer>
			}
			label="Custom"
			onClick={onClick}
			isSelected={isSharingCustom}
		/>
	);
}

type SharingOptionButtonProps = {
	icon?: JSX.Element;
	label: string;
	description?: string;
	onClick: () => void;
	isSelected?: boolean;
} & Partial<ClassNameProps & StyleProps>;

function SharingOptionButton({
	icon,
	label,
	description,
	onClick,
	isSelected = false,
	className,
	style,
}: SharingOptionButtonProps) {
	return (
		<Button
			onClick={onClick}
			className={makeClassName(
				styles["sharing-option"],
				styles["sharing-option-button"],
				isSelected ? styles["sharing-option--selected"] : "",
				description ? styles["sharing-option--with-description"] : "",
				className,
			)}
			style={style}
		>
			<Row justify="spaced-start" align="center" className={styles["sharing-option__content"]}>
				{icon}

				<Column className={styles["sharing-option__column"]}>
					<TruncatedText>{label}</TruncatedText>

					{!!description && (
						<TruncatedText className={styles["sharing-option__description"]}>{description}</TruncatedText>
					)}
				</Column>
			</Row>
		</Button>
	);
}
